<template>
  <header :class="['report-menu',scrolled?'scrolled':'']">
    <div class="menu-float-bar" ref="menu-bar">
      <div class="school-name skiptranslate notranslate">{{selectedSchoolDisplayName}}</div>
      <button
        class="menu-toggle open"
        role="button"
        :aria-expanded="menuOpened"
        aria-controls="main-menu"
        aria-label="Open main menu"
        @click="openMenu"
        >
        <span class="sr-only">Open main menu</span>
        <font-awesome-icon icon="bars" class="icon"/>
        <div class="label" aria-hidden="true">Menu</div>
      </button>
    </div>

    <nav id="main-menu"
      :class="['menu-overlay',menuOpened?'is-active':'']"
      role="navigation"
      :aria-expanded="menuOpened"
      aria-label="Main menu"
      >
      <div class="wrapper">
        <div class="bg" @click="closeMenu"></div>
        <div class="top-bar">
          <div class="title">Menu</div>
          <button class="menu-toggle close"
            role="button"
            :aria-expanded="menuOpened"
            aria-controls="main-menu"
            aria-label="Close main menu"
            @click="closeMenu"
            >
            <span class="sr-only">Close main menu</span>
            <font-awesome-icon icon="times" class="icon"/>
          </button>
        </div>
        <div class="links">
          <ul>
            <template v-for="c in selectedSchoolTabCodes">
              <li :key="'link-'+c" :class="colorClassForCode(c)">
                <a :href="'#'+c" @click.prevent="handleMenuItemClicked(c)"><div>{{ tabNameForCode(c) }}</div></a>
              </li>
              <!-- For now assume only 2 column layout, only add in those break points -->
              <li v-if="breakAfterCodesByNumColumns[2].indexOf(c)>-1" :key="'break-'+c" class="column-break"></li>
            </template>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { mapGetters } from 'vuex'
import Component, { mixins } from 'vue-class-component'

import { TabNavMixin } from '@/mixin'
import { NS_CURRENT_REPORT } from './const'
import { frElementNameForCode, colorClassForCode } from '@/utils/data'

/*
Layout of menu items, by number of columns available
Notes:
- Actual display order is determined by order in vuex store (to be consistent
    across whole report)
- Will be intersected with actual available tab codes to find where to put
    break points
- If somehow no items are left in a column, column break may be ignored
*/
const MENU_LAYOUTS = {
  2: [
    ['INFO', 'SA', 'RI', 'CT', 'SE'],
    ['ES', 'SF', 'TR']
  ],
  3: [
    ['INFO'],
    ['SA', 'RI', 'CT', 'SE'],
    ['ES', 'SF', 'TR']
  ]
}
const MENU_LAYOUT_SETS = Object.keys(MENU_LAYOUTS).reduce((a, d) => {
  a[d] = []
  for (let i = 0; i < d; i++) {
    a[d].push(new Set())
    MENU_LAYOUTS[d][i].forEach(c => a[d][i].add(c))
  }
  return a
}, {})

@Component({
  computed: {
    ...mapGetters(NS_CURRENT_REPORT, [
      'selectedSchoolDisplayName',
      'selectedSchoolTabCodes'
    ])
  },
  methods: {
    colorClassForCode
  }
})
export default class ReportMenu extends mixins(TabNavMixin) {
  scrolled = false
  menuOpened = false

  /* Lifecycle */

  created () {
    window.addEventListener('scroll', this.handleScroll)
  }
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  }
  handleScroll () {
    this.scrolled = window.pageYOffset > 0
  }

  /* Getters */

  // Lookup of numColumns: [lastCodeCol1,...]
  get breakAfterCodesByNumColumns () {
    const lastCodesByNumColumns = this.lastCodesByNumColumns

    const breakCodesByNumColumns = {}
    for (let numColumns in lastCodesByNumColumns) {
      const codes = lastCodesByNumColumns[numColumns]

      // Don't break after last column
      codes.pop()

      // Flatten results, ignoring empty columns
      breakCodesByNumColumns[numColumns] = codes.reduce((a, c) => {
        if (c.length > 0) a.push(c[0])
        return a
      }, [])
    }
    return breakCodesByNumColumns
  }

  // Lookup of numColumns: [[lastCodeCol1],[lastCodeCol2],...]
  get lastCodesByNumColumns () {
    const rc = this.selectedSchoolTabCodes.slice().reverse()

    const lastCodes = {}
    for (let numColumns in MENU_LAYOUT_SETS) {
      const sets = MENU_LAYOUT_SETS[numColumns]

      // Initialize dictionary of array of arrays of empty results
      const result = sets.reduce((a, d) => { a.push([]); return a }, [])

      // Find last code in each column
      rc.forEach(code => {
        sets.forEach((s, i) => {
          if (result[i].length > 0) return // Already found last code in column
          if (s.has(code)) result[i].push(code)
        })
      })

      lastCodes[numColumns] = result
    }
    return lastCodes
  }

  /* Methods - utility */
  openMenu () {
    this.menuOpened = true
  }
  closeMenu () {
    this.menuOpened = false
  }
  tabNameForCode (c) {
    return c === 'INFO' ? 'School Info' : frElementNameForCode(c)
  }
  handleMenuItemClicked (hash) {
    this.closeMenu()
    this.navigateToTab(hash)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import './css/const.scss';

$col-width: 240px;
$col-spacing: 24px;
$close-button-width: 30px;

$content-width-2-col: 2*$col-width + $col-spacing;
$content-width-1-col: $col-width;

.report-menu {
  display: none;
  position: absolute;
  top: 0px;
  width: 100%;
  z-index: 1;
  > * {
    width: 100%;
    box-sizing: border-box;
    padding: 16px 10px 24px 10px;
  }
}
.menu-float-bar {
  display: flex;
  justify-content: flex-end;
  position: absolute;
}
.school-name {
  flex: 1;
  float: left;
  display: none;
  padding-right: 0px;
}
.menu-toggle {
  /* Disable button-ish features */
  height: fit-content;
  border: none;
  background-color: transparent;

  float: right;
  color: #555555;
  text-align: center;
  cursor: pointer;
  .icon {
    font-size: 40px;
  }
}

.report-menu.scrolled {
  z-index: 10000002; /* To be on top of google translate */
  position: fixed;
  > * {
    padding-top: 24px;
  }
  .menu-float-bar {
    background-color: white;
    box-shadow: 0px 5px 10px #aaaaaa;
  }
  .school-name {
    display: block;
  }
}

#main-menu {
  display: none;
  position: fixed;
  padding: 0px;
  width: 100%;
  height: 100%;
  &.is-active {
    display: block;
  }
  .wrapper,
  .bg {
    position: absolute;
    width: 100%;
    height: 100%;
  }
  .bg {
    background-color: #555555;
    opacity: 0.8;
  }
  .top-bar {
    position: relative;
    width: 100%;
    height: 36px;
    padding-right: 10px;
    box-sizing: border-box;
    display: flex;
    justify-content: flex-end;
    background-color: #333333;
    color: white;
  }
  .title {
    font-size: 16px;
    font-weight: bold;
    margin-top: 10px;
    display: inline-block;
    width: $content-width-2-col - $close-button-width;
  }
  .menu-toggle {
    float: none;
    width: $close-button-width;
  }
  .icon {
    color: white;
    font-size: 24px;
    padding-top: 4px;
  }
  .links {
    position: relative;
    background-color: white;
    padding: 0px 10px 16px 0px;
    display: flex;
    justify-content: flex-end;
    ul {
      margin: 0px;
      padding: 0px;
      list-style-type: none;
      width: $content-width-2-col;
      max-height: 250px;
      display: flex;
      flex-flow: column wrap;
    }
    li {
      background-color: transparent;
      border-bottom-style: solid;
      border-bottom-width: 1px;
      border-bottom-color: black;
      width: $col-width;
      &:not(.column-break):hover {
        background-color: #eeeeee;
      }
      a {
        color: black;
        text-decoration: none;
      }
      div {
        width: 100%;
        padding-top: 24px;
        font-size: 16px;
        font-weight: bold;
      }
      &.column-break {
        height: 100%;
        width: $col-spacing;
        border-bottom-width: 0px;
      }
    }
  }
}

/* Responsive rules */

@media screen and (max-width: $screen-width-2-column-small) {
.report-menu {
  display: block;
}
}
@media screen and (max-width: $screen-width-1-column-large) {
.menu-toggle {
  .icon {
    font-size: 36px;
  }
  .label {
    font-size: 12px;
  }
}
}
@media screen and (max-width: $screen-width-1-column-medium) {
#main-menu {
  .title {
    width: $content-width-1-col - $close-button-width;
  }
  .links {
    ul {
      width: $content-width-1-col;
      max-height: none;
    }
    li.column-break {
      display: none;
    }
  }
}
}
</style>



// WEBPACK FOOTER //
// src/components/sqs/2023/ReportMenu.vue